import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import employeeDeploymentService from '../../../services/employeeDeploymentService';

const TransferDeploymentModal = ({ isOpen, onClose, deployment, clientSites, onTransfer }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    new_client_site_id: '',
    transfer_date: '',
    new_deployment_end_date: '',
    notes: ''
  });

  useEffect(() => {
    if (deployment && isOpen) {
      // Set transfer date to today by default
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        new_client_site_id: '',
        transfer_date: today,
        new_deployment_end_date: '',
        notes: `Transferred from ${deployment.site_name}`
      });
    }
    setError(null);
  }, [deployment, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!deployment) {
      setError('Deployment information is missing');
      return;
    }

    if (!formData.new_client_site_id) {
      setError('New client site is required');
      return;
    }

    if (!formData.transfer_date) {
      setError('Transfer date is required');
      return;
    }

    // Validate that new site is different from current
    if (formData.new_client_site_id === deployment.client_site_id) {
      setError('New client site must be different from current site');
      return;
    }

    // Validate dates
    if (formData.new_deployment_end_date && formData.new_deployment_end_date < formData.transfer_date) {
      setError('New deployment end date must be after transfer date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const transferData = {
        deployment_id: deployment.id,
        new_client_site_id: formData.new_client_site_id,
        transfer_date: formData.transfer_date,
        new_deployment_end_date: formData.new_deployment_end_date || null,
        notes: formData.notes || ''
      };

      const result = await employeeDeploymentService.transferDeployment(transferData);

      if (result?.success) {
        onTransfer?.();
      } else {
        setError(result?.error || 'Failed to transfer deployment');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error('Deployment transfer error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !deployment) return null;

  const currentSite = clientSites.find(s => s.id === deployment.client_site_id);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-background rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-foreground">
            Transfer Deployment
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Current Deployment Info */}
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h4 className="text-sm font-semibold text-foreground mb-2">Current Deployment</h4>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <Icon name="User" size={14} className="text-muted-foreground" />
              <span className="text-foreground font-medium">{deployment.full_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={14} className="text-muted-foreground" />
              <span className="text-foreground">{deployment.site_name} - {deployment.company_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
              <span className="text-foreground">
                Since {new Date(deployment.deployment_start_date).toLocaleDateString('en-IN')}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                New Client Site *
              </label>
              <select
                value={formData.new_client_site_id}
                onChange={(e) => handleInputChange('new_client_site_id', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                required
              >
                <option value="">Select New Client Site</option>
                {clientSites
                  .filter(site => site.id !== deployment.client_site_id) // Exclude current site
                  .map(site => (
                    <option key={site.id} value={site.id}>
                      {site.site_name} - {site.company_name}
                    </option>
                  ))}
              </select>
            </div>

            <Input
              label="Transfer Date *"
              type="date"
              value={formData.transfer_date}
              onChange={(e) => handleInputChange('transfer_date', e.target.value)}
              required
              min={new Date().toISOString().split('T')[0]}
            />

            <Input
              label="New Deployment End Date (Optional)"
              type="date"
              value={formData.new_deployment_end_date}
              onChange={(e) => handleInputChange('new_deployment_end_date', e.target.value)}
              min={formData.transfer_date}
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-foreground mb-2">
                Transfer Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                rows="3"
                placeholder="Notes about this transfer"
              />
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium mb-1">What happens when you transfer:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Current deployment will be marked as "transferred"</li>
                  <li>A new active deployment will be created at the new site</li>
                  <li>Employee's current deployment will be updated automatically</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  Transferring...
                </>
              ) : (
                <>
                  <Icon name="ArrowRight" size={16} className="mr-2" />
                  Transfer Deployment
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferDeploymentModal;

